import { useEffect, useMemo, useState } from "react";

import { Container, Stack } from "@mui/material";

import SearchHeader from "./SchwarzesBrettAnsicht/SearchHeader";
import BoardTabs from "./SchwarzesBrettAnsicht/BoardTabs";
import FilterChips from "./SchwarzesBrettAnsicht/FilterChips";
import SchwarzesBrettCard from "./SchwarzesBrettAnsicht/SchwarzesBrettCard";
import EmptyState from "./SchwarzesBrettAnsicht/EmptyState";

import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import apiRequest from "../../functions/api";
import { useSessionStorage } from "../../utils/hooks";
import { LOCAL_STORAGE_KEY } from "../../constants/localStorage";
import { useTranslation } from "react-i18next";
import { useMessage } from "../../context/message-context";
import axios from "axios";
import { API_BASE_URL } from "../../constants/apiConstants";
import openFile from "../shared/functions/download";
import DetailDialog from "./SchwarzesBrettAnsicht/DetailDialog";
import ConfirmDialog from "./SchwarzesBrettAnsicht/ConfirmDialog";

const program = "BRETTL";

export function SchwarzesBrettAnsicht({ user }) {
  const [accessToken] = useSessionStorage(LOCAL_STORAGE_KEY.accessToken);
  const { t } = useTranslation();
  const { showErrorMessage, showSuccessMessage } = useMessage();

  const [category, setCategory] = useState("company");
  const [filter, setFilter] = useState("Aktiv");
  const [search, setSearch] = useState("");
  const [daten, setDaten] = useState();
  const [selectedItem, setSelectedItem] = useState({});
  const [detailDialog, setDetailDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(false);
  const [mitarbeiterBereich, setMitarbeiterBereich] = useState(false); // Wird über ZPMS-SCH29 verwaltet

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    const payload = {
      statement: "LADEN_AKTIV",
      program: program,
      alleLaden: 1,
    };
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    apiRequest("POST", "schwarzes_brett-anfrage", payload, headers, loadCb);
  };

  const loadCb = (response) => {
    if (response.status === 200) {
      setDaten(response.data.items);

      if (response.data.zul === "1") {
        setMitarbeiterBereich(true);
      } else {
        setMitarbeiterBereich(false);
      }
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const openDatei = (event, file) => {
    event.stopPropagation();
    const payload = {
      ffe3_key: file,
      program: program,
      statement: "GET_FILE_DATA",
    };

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    axios
      .post(API_BASE_URL + "schwarzes_brett-anfrage", payload, { headers: headers, responseType: "blob" })
      .then((response) => {
        var fileName = response.headers["content-disposition"].match(/filename="?([^"]+)"?/)[1];
        var contentFile = response.data;
        openFile(fileName, contentFile);
      })
      .catch(function (error) {
        showErrorMessage("Ein Fehler ist aufgetreten. Bitte versuchen Sie den download erneut. Fehler: " + error);
      });
  };

  const openDetails = (event, item) => {
    if (item?.meldung === "<p></p>" && item?.datei !== "") {
      openDatei(event, item.datei);
    } else {
      setSelectedItem(item);
      setDetailDialog(true);
    }
  };

  const closeDetails = () => {
    setDetailDialog(false);
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);

    if (newCategory === "company") {
      setFilter("Aktiv");
    }

    if (newCategory === "employee") {
      setFilter("Mitarbeiter");
    }
  };

  const onChangeMitarbeiter = () => {
    // Einträge neu Laden weil einer hinzugefügt wurde
    loadAll();
  };

  const filterOptions = useMemo(() => {
    return {
      company: [
        {
          label: "Aktuell",
          value: "Aktiv",
          allowed: "Aktiv",
        },
        {
          label: "Daueraushang",
          value: "Zeitlos",
          allowed: "Zeitlos",
        },
        {
          label: "Vergangen",
          value: "Inaktiv",
          allowed: "Inaktiv",
        },
      ],

      ...(mitarbeiterBereich && {
        employee: [
          {
            label: "Alle Meldungen",
            value: "Mitarbeiter",
            allowed: "Mitarbeiter",
          },
          {
            label: "Meine Meldungen",
            value: "eigene",
            allowed: "Mitarbeiter",
          },
        ],
      }),
    };
  }, [mitarbeiterBereich]);

  const availableFilters = filterOptions[category];

  const filteredData = useMemo(() => {
    const allowedStatus = filterOptions[category]?.find((item) => item.value === filter)?.allowed;

    const result = daten
      ?.filter((item) => {
        const tmpStatus = item.status === allowedStatus;
        const tmpSuche = item.betreff.toLowerCase().includes(search.toLowerCase());
        const tmpErsteller = filter === "eigene" ? item.ersteller === user.id : item;

        return tmpStatus && tmpSuche && tmpErsteller;
      })
      .sort((a, b) => {
        if (filter === "Zeitlos") {
          return a.betreff.localeCompare(b.betreff, "de", {
            sensitivity: "base",
          });
        }

        return b.erstellDat.localeCompare(a.erstellDat);
      });

    return result;
  }, [category, filter, search, daten]);

  const startDelete = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setItemToDelete(item);
    setConfirmDialog(true);
  };

  const deleteEintrag = () => {
    if (itemToDelete == null) return;
    setDetailDialog(false);

    const payload = {
      statement: "LADEN_AKTIV",
      aktion: "eintragEntfernen",
      program: program,
      key: itemToDelete.key,
    };
    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    apiRequest("POST", "schwarzes_brett-anfrage", payload, headers, deleteCb);
  };

  const deleteCb = (response) => {
    if (response.status === 200) {
      loadAll();
      showSuccessMessage(t("Löschen erfolgreich"));
    } else {
      showErrorMessage(response.data.message);
    }
  };

  return (
    <Page>
      <Layout>
        <Container
          maxWidth="lg"
          sx={{
            py: {
              xs: 1,
              sm: 3,
              md: 5,
            },

            px: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Stack spacing={3}>
            <SearchHeader
              search={search}
              onSearchChange={setSearch}
              onCreate={category === "employee" ? () => {} : null}
              onChangeMitarbeiter={onChangeMitarbeiter}
            />

            <BoardTabs value={category} onChange={handleCategoryChange} mitarbeiterBereich={mitarbeiterBereich} />

            <FilterChips filters={availableFilters} value={filter} onChange={setFilter} />

            <DetailDialog
              openDatei={openDatei}
              open={detailDialog}
              onClose={closeDetails}
              item={selectedItem}
              filter={filter}
              user={user}
              startDelete={startDelete}
            />

            <ConfirmDialog
              confirmDialog={confirmDialog}
              setConfirmDialog={setConfirmDialog}
              itemToDelete={itemToDelete}
              setItemToDelete={setItemToDelete}
              deleteEintrag={deleteEintrag}
            ></ConfirmDialog>

            <Stack spacing={2}>
              {filteredData?.length > 0 ? (
                filteredData?.map((item) => (
                  <SchwarzesBrettCard
                    key={item.key}
                    item={item}
                    openDetails={openDetails}
                    openDatei={openDatei}
                    filter={filter}
                    user={user}
                    startDelete={startDelete}
                  />
                ))
              ) : (
                <EmptyState
                  onReset={() => {
                    setSearch("");
                    // setFilter(category === "company" ? "current" : "all");
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Container>
      </Layout>
    </Page>
  );
}
